import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export enum Role {
  ADMIN = 'admin',
  SUPER_ADMIN = 'super_admin',
  OPS = 'ops',
  FINANCE = 'finance',
  SUPPORT = 'support',
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  avatar?: string;
  phone?: string;
  createdAt: string;
  is_super_admin?: boolean;
  must_change_password?: boolean;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setUser: (user: User) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  login: (user: User, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;

  // Permission helpers
  hasPermission: (permission: string) => boolean;
  hasRole: (role: Role | Role[]) => boolean;
}

// Role-based permissions
const rolePermissions: Record<Role, string[]> = {
  [Role.ADMIN]: ['*'],
  [Role.SUPER_ADMIN]: ['*'],
  [Role.OPS]: ['dashboard', 'customers', 'drivers', 'bookings', 'support'],
  [Role.FINANCE]: ['dashboard', 'payments', 'reports', 'pricing'],
  [Role.SUPPORT]: ['dashboard', 'customers', 'bookings', 'support'],
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,

      setUser: (user: User) => set({ user }),

      setTokens: (accessToken: string, refreshToken: string) =>
        set({ accessToken, refreshToken }),

      login: (user: User, accessToken: string, refreshToken: string) =>
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        }),

      setLoading: (isLoading: boolean) => set({ isLoading }),

      hasPermission: (permission: string) => {
        const { user } = get();
        if (!user) return false;

        const permissions = rolePermissions[user.role];
        
        // If role not found in rolePermissions, grant all permissions (fallback for admin/unknown roles)
        if (!permissions) {
          console.warn(`Unknown role: ${user.role}. Granting all permissions as fallback.`);
          return true;
        }
        
        if (permissions.includes('*')) return true;

        return permissions.includes(permission);
      },

      hasRole: (role: Role | Role[]) => {
        const { user } = get();
        if (!user) return false;

        if (Array.isArray(role)) {
          return role.includes(user.role);
        }
        return user.role === role;
      },
    }),
    {
      name: 'zipto-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Set isLoading to false after rehydration completes
        if (state) {
          state.isLoading = false;
        }
      },
    }
  )
);

// Utility functions
export const getPermissionsForRole = (role: Role): string[] => {
  return rolePermissions[role];
};

export const canAccessRoute = (route: string, role: Role): boolean => {
  const permissions = rolePermissions[role];
  if (permissions.includes('*')) return true;

  // Map routes to permissions
  const routePermissionMap: Record<string, string> = {
    '/dashboard': 'dashboard',
    '/customers': 'customers',
    '/drivers': 'drivers',
    '/bookings': 'bookings',
    '/pricing': 'pricing',
    '/payments': 'payments',
    '/support': 'support',
    '/reports': 'reports',
  };

  const requiredPermission = Object.entries(routePermissionMap).find(([path]) =>
    route.startsWith(path)
  )?.[1];

  if (!requiredPermission) return true; // Public routes
  return permissions.includes(requiredPermission);
};
